import React from 'react';
import { MattressProduct, ChatConfig } from '../types';

// Update default image path to reference the file we just copied to the public directory
const DEFAULT_IMAGE_URL = '/default-product.jpg';

interface ProductCardProps {
  product: MattressProduct;
  onClick: (product: MattressProduct) => void;
  config: ChatConfig | null;
  matchTags?: Array<{ label: string; value: boolean | string }>;
}

const ProductCard: React.FC<ProductCardProps> = ({ product, onClick, config, matchTags = [] }) => {
  const primaryColor = config?.branding?.colors?.primary || '#2563eb';
  
  const handleClick = () => {
    onClick(product);
  };

  // Get firmness display text
  const getFirmnessText = (firmness: string | undefined) => {
    if (!firmness) return null;
    
    switch (firmness) {
      case 'soft': return 'Soft';
      case 'medium-soft': return 'Medium Soft';
      case 'medium': return 'Medium';
      case 'medium-firm': return 'Medium Firm';
      case 'firm': return 'Firm';
      default: return firmness;
    }
  };

  // Ensure productUrl has a proper protocol
  const getFullProductUrl = (url: string): string => {
    if (!url) return '#';
    if (url.match(/^https?:\/\//)) return url;
    return `https://${url}`;
  };

  // Render product badges
  const renderBadge = (label: string | number | null | undefined) => {
    if (label === null || label === undefined) return null;
    
    return (
      <span className="inline-flex items-center text-xs px-2 py-0.5 bg-gray-100 rounded-full">
        {label}
      </span>
    );
  };

  // Function to create branded placeholder for missing images
  const placeholderImage = () => {
    const primaryColor = config?.branding?.colors?.primary || '#2563eb';
    const secondaryColor = config?.branding?.colors?.secondary || '#64748b';
    
    // Create a data URL for a branded placeholder SVG
    return `data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="300" height="200" viewBox="0 0 300 200">
      <rect width="300" height="200" fill="${primaryColor.replace('#', '%23')}" opacity="0.1" />
      <text x="150" y="100" font-family="Arial, sans-serif" font-size="16" fill="${secondaryColor.replace('#', '%23')}" text-anchor="middle" dominant-baseline="middle">
        ${product.name.replace(/[<>&]/g, '')}
      </text>
      <text x="150" y="130" font-family="Arial, sans-serif" font-size="14" fill="${secondaryColor.replace('#', '%23')}" text-anchor="middle" dominant-baseline="middle">
        ${product.specifications?.type ? product.specifications.type.replace(/[<>&-]/g, ' ') : 'Mattress'} ${product.specifications?.firmness ? `- ${getFirmnessText(product.specifications.firmness)}` : ''}
      </text>
    </svg>`;
  };

  // Extract specifications safely with optional chaining
  const specs = product.specifications || {};
  const firmness = getFirmnessText(specs.firmness);
  const type = specs.type ? specs.type.replace('-', ' ') : null;
  const height = specs.height ? `${specs.height}"` : null;
  const warranty = specs.warranty || null;

  // Format price with commas
  const formatPrice = (price: number): string => {
    return price.toLocaleString('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    });
  };

  // Format firmness for display
  const formatFirmness = (firmness: string): string => {
    return firmness ? firmness.replace(/-/g, ' ') : '';
  };

  return (
    <div 
      className="product-card w-full bg-white border border-gray-200 rounded-lg overflow-hidden shadow-sm hover:shadow-md transition-shadow cursor-pointer flex flex-col" 
      style={{ maxWidth: '300px' }}
      onClick={handleClick}
    >
      {/* Image Container */}
      <div className="relative bg-gray-50 p-4 flex items-center justify-center" style={{ minHeight: '180px' }}>
        {product.imageUrl ? (
          <img 
            src={product.imageUrl} 
            alt={product.name} 
            className="max-h-40 object-contain"
            onError={(e) => {
              // If product image fails to load, use our fallback image
              (e.target as HTMLImageElement).src = DEFAULT_IMAGE_URL;
            }}
          />
        ) : (
          // Use the default image instead of an SVG icon
          <img 
            src={DEFAULT_IMAGE_URL} 
            alt={product.name || "Mattress product"} 
            className="max-h-40 object-contain"
          />
        )}
        
        {/* Firmness badge */}
        {product.specifications?.firmness && (
          <div 
            className="absolute top-2 left-2 px-3 py-1 rounded-full border-2 bg-white text-xs font-semibold"
            style={{ 
              borderColor: primaryColor,
              color: primaryColor
            }}
          >
            {formatFirmness(product.specifications.firmness)}
          </div>
        )}
        
        {/* Type badge */}
        {product.specifications?.type && (
          <div className="absolute bottom-2 left-2 px-2 py-1 bg-gray-100 text-gray-600 rounded text-xs">
            {product.specifications.type.replace('-', ' ')}
          </div>
        )}
      </div>
      
      {/* Product Info */}
      <div className="p-4 flex-1 flex flex-col">
        <h3 className="text-lg font-semibold text-gray-900 line-clamp-2 mb-1">{product.name}</h3>
        
        {/* Display match tags */}
        {matchTags && matchTags.length > 0 && (
          <div className="flex flex-wrap gap-1 mb-2">
            {matchTags.map((tag, index) => (
              <span 
                key={`match-${index}`}
                className="inline-block px-2 py-1 text-xs font-medium bg-green-100 text-green-800 rounded-full"
              >
                {tag.label}
              </span>
            ))}
          </div>
        )}
        
        <p className="text-sm text-gray-600 line-clamp-2 mb-3 flex-1">{product.description}</p>
        
        {/* Price and features */}
        <div className="mt-auto">
          {product.price > 0 && (
            <div className="text-lg font-bold mb-2">
              {formatPrice(product.price)}
            </div>
          )}
          
          {/* Features list - show up to 3 */}
          {product.features && product.features.length > 0 && (
            <ul className="text-sm text-gray-700">
              {product.features.slice(0, 3).map((feature, idx) => (
                <li key={idx} className="flex items-start mb-1">
                  <svg className="h-4 w-4 mt-0.5 mr-1 flex-shrink-0" style={{ color: primaryColor }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                  </svg>
                  <span className="line-clamp-1">{feature}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
      
      {/* Button */}
      <div className="px-4 pb-4">
        <button 
          className="w-full py-2 px-4 border border-transparent rounded-md text-sm font-medium border-2"
          style={{ borderColor: primaryColor, color: primaryColor }}
          onClick={(e) => {
            e.stopPropagation();
            if (product.productUrl) {
              const url = product.productUrl.match(/^https?:\/\//) ? product.productUrl : `https://${product.productUrl}`;
              window.open(url, '_blank', 'noopener,noreferrer');
            }
          }}
        >
          View Details
        </button>
      </div>
    </div>
  );
};

export default ProductCard; 